"use client";

import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import SourceCard from "@/app/(components)/generics/cards/SourceCard/SourceCard";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanRunPaidActions } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKeyClientAvatar } from "@/app/translations/keys/TKeyClientAvatar";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProjectOfferData } from "@/app/types/client-avatar/UserClientAvatar";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { StepState } from "./ExtractAvatar.logic";
import styles from "./ExtractAvatar.module.css";

type Props = DefaultProps & {
  atLeastOneAnalysedCall: boolean | undefined;
  offers: ProjectOfferData[] | undefined;
  onExtract: () => void;
  isAnalyzing?: boolean;
};

export default function ExtractAvatar(props: Props) {
  const T = useT();
  const { params, atLeastOneAnalysedCall, offers, onExtract, isAnalyzing } = props;

  const hasOffer = offers !== undefined && offers.length > 0;

  const user = useAuthUser();
  const openSignUp = () => ModalStore.getState().openSignUp(params);

  const canRunPaidActions = useCanRunPaidActions();
  if (!canRunPaidActions) {
    return null;
  }

  const button = (
    <Button
      min={true}
      isLink={false}
      type={ButtonType.PRIMARY}
      text={T(TKey.CLIENT_AVATAR_EXTRACT_BUTTON) as string}
      beforeIcon={user && isAnalyzing ? <CircleLoader size={16} color="currentColor" /> : undefined}
      onClick={!user ? openSignUp : onExtract}
    />
  );

  const renderStep = (num: number, labelKey: TKey, state: StepState) => (
    <div className={UStyles.classNames(styles.step, state === StepState.DONE && styles.stepDone)}>
      <span
        className={UStyles.classNames(
          styles.stepNumber,
          state === StepState.DONE && styles.stepNumberDone,
        )}
      >
        {state === StepState.DONE ? <IconCheck /> : num}
      </span>
      <span className={styles.stepLabel}>{T(labelKey)}</span>
    </div>
  );

  const cardTitle = T(TKey.CLIENT_AVATAR_EXTRACT_TITLE) as string;
  const cardBadge = T(TKey.CLIENT_AVATAR_EXTRACT_BADGE) as string;

  if (user && atLeastOneAnalysedCall === true && hasOffer) {
    return (
      <SourceCard
        title={cardTitle}
        badge={cardBadge}
        actions={
          <Button
            min={true}
            isLink={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.CLIENT_AVATAR_EXTRACT_BUTTON) as string}
            onClick={onExtract}
            beforeIcon={isAnalyzing ? <CircleLoader size={16} color="currentColor" /> : undefined}
          />
        }
      >
        <div className={styles.steps}>
          {renderStep(1, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_1, StepState.DONE)}
          {renderStep(2, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_2, StepState.DONE)}
          {renderStep(3, TKeyClientAvatar.CLIENT_AVATAR_EXTRACT_STEP_3, StepState.ACTIVE)}
        </div>
      </SourceCard>
    );
  }

  return (
    <SourceCard
      title={cardTitle}
      badge={cardBadge}
      description={T(TKey.CLIENT_AVATAR_EXTRACT_DESCRIPTION) as string}
      benefits={[
        T(TKey.CLIENT_AVATAR_EXTRACT_BENEFIT_1) as string,
        T(TKey.CLIENT_AVATAR_EXTRACT_BENEFIT_2) as string,
        T(TKey.CLIENT_AVATAR_EXTRACT_BENEFIT_3) as string,
      ]}
      actions={button}
    />
  );
}
